import { Box, Button, useDisclosure } from '@chakra-ui/react'
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sumBy } from 'lodash-es'

import {
  SAT_BOOTCAMP_TUTOR_ONLY_SERIES_TEMPLATE_SLUGS,
  SeriesWithdrawalSource,
} from '@/queries/constants'
import SeriesQ from '@/queries/SeriesQ'
import SeriesRegistrationQ from '@/queries/SeriesRegistrationQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { type Query, SQL } from '@/client/query'
import { trackEvent } from '@/client/services/analytics'
import { resolveMaxRegistrationsPerLearner } from '@/client/services/series/helpers'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import Tippy from '@/components/UI/Tippy'

import SeriesRegistrationModal from './SeriesRegistrationModal'

export default function SeriesRegistrationButton({
  series,
  isUserSelfReserved,
}: {
  series: Query
  isUserSelfReserved: boolean
}): JSX.Element | null {
  const user = useUser()
  const query = useQuery()

  const registrationWizard = useDisclosure()

  const me = query.one(UserQ.where({ id: user.id }))

  const isVerified = me(UserQ.isVerified()) ?? false

  const isReservedByFriend = series(SeriesQ.isReservedByFriend(user.id)) ?? false

  const seriesRegistration = series.one(SeriesQ.getMostRecentRegistration(user.id))

  const hasSessionInProgress =
    series<boolean>`EXISTS(SELECT 1 ${SessionQ.where(
      { seriesId: SeriesQ.id },
      SessionQ.isInProgress()
    )})` ?? false

  const maxLearners = series(SeriesQ.max_learners) ?? 0
  const countRegistrations = series(SeriesQ.countRegistrations()) ?? 0
  const countActiveReservations = series(SeriesQ.countActiveReservations()) ?? 0
  const isFull = countRegistrations + countActiveReservations >= maxLearners

  const isEnded = series(SeriesQ.isClosed()) ?? false
  const isEnrollmentClosed = series(SeriesQ.isEnrollmentClosed()) ?? false
  const didTutorWithdrawUser =
    seriesRegistration(SeriesRegistrationQ.withdrawal_source) === SeriesWithdrawalSource.Tutor

  const seriesTemplate = series.one(SeriesTemplateQ.where({ id: SeriesQ.series_template_id }))
  const seriesTemplateId = seriesTemplate(SeriesTemplateQ.id)
  const seriesTemplateSlug = seriesTemplate(SeriesTemplateQ.slug)
  const maxRegistrationsPerLearner = resolveMaxRegistrationsPerLearner(
    seriesTemplate(SeriesTemplateQ.max_registrations_per_learner)
  )
  const userSeriesRegistrations = me.many(
    SeriesRegistrationQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'Series',
          required: true,
          where: SQL`NOT ${SeriesQ.isClosed()}`,
        },
      ],
    })
  )
  const countRegistrationsForTemplate = sumBy(userSeriesRegistrations, registration =>
    registration(SeriesQ.series_template_id) === seriesTemplateId ? 1 : 0
  )

  const hasTooManyRegistrationsInSeriesTemplate =
    countRegistrationsForTemplate >= maxRegistrationsPerLearner

  const canRegisterForSATTutorSessions = user.satBootcampStates.some(s => s.type === 'tutor')

  const isSeriesForSATTutors =
    seriesTemplateSlug != null &&
    SAT_BOOTCAMP_TUTOR_ONLY_SERIES_TEMPLATE_SLUGS.includes(seriesTemplateSlug)

  let disabledTooltipText: string | null = null
  if (isFull && !isReservedByFriend) {
    disabledTooltipText = 'Series is full.'
  } else if (!isVerified && hasSessionInProgress) {
    disabledTooltipText = 'You cannot register for a series when a session is in progress.'
  } else if (didTutorWithdrawUser) {
    disabledTooltipText = 'You were withdrawn from this series.'
  } else if (isEnded) {
    disabledTooltipText = 'Series has ended.'
  } else if (isEnrollmentClosed) {
    disabledTooltipText = 'Enrollment has closed.'
  } else if (hasTooManyRegistrationsInSeriesTemplate) {
    disabledTooltipText = `You can only register for
      ${maxRegistrationsPerLearner} series of this program at a time! If you'd like to register for this one, please unregister from another one first.`
  } else if (isSeriesForSATTutors && !canRegisterForSATTutorSessions) {
    disabledTooltipText = 'Only SAT Bootcamp tutors can register for this kind of series.'
  }

  let registerButtonText = 'Register'
  if (isReservedByFriend) {
    registerButtonText = 'Complete Reservation'
  }

  const registrationButton = (
    <Button
      isDisabled={disabledTooltipText != null}
      onClick={() => {
        if (disabledTooltipText != null) {
          return
        }

        registrationWizard.onOpen()

        trackEvent({
          category: 'SAT Bootcamp - Registration',
          action: 'modal opened',
        })
      }}
      leftIcon={<FontAwesomeIcon icon={faUserPlus} />}
      width="100%"
    >
      {registerButtonText}
    </Button>
  )

  return (
    <>
      {!isUserSelfReserved ? (
        <Tippy content={disabledTooltipText} disabled={disabledTooltipText == null}>
          <Box>
            <RequireLoginAndApproval>{registrationButton}</RequireLoginAndApproval>
          </Box>
        </Tippy>
      ) : null}

      <SeriesRegistrationModal
        open={registrationWizard.isOpen}
        onClose={registrationWizard.onClose}
        series={series}
      />
    </>
  )
}
